import Axios from 'axios'
// import { queryFeatures } from '@esri/arcgis-rest-feature-layer'

import { getConfig, getConfigs } from './config'
import { IMAGERY_METADATA_FIELDS } from './constants'

const CancelToken = Axios.CancelToken
const source = CancelToken.source()
/***********************************
 * findChanges Helper Functions
 ***********************************/
/**** Step 1 ****/
const latitudeToTile = ({ lat, zoom }) => {
  return Math.floor(((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) * Math.pow(2, zoom))
}
const longitudeToTile = ({ lon, zoom }) => {
  return Math.floor(((lon + 180) / 360) * Math.pow(2, zoom))
}
const getTilesFromPointCoordinates = ({ point }) => {
  const level = +point.zoom.toFixed(0)
  const column = longitudeToTile({ lon: point.longitude, zoom: level })
  const row = latitudeToTile({ lat: point.latitude, zoom: level })
  return {
    column,
    level,
    row
  }
}

/**** Step 2 ****/
const setItemIdDictionary = ({ imageryList }) => {
  const itemIdDictionary = {}

  for (let ili = 0; ili < imageryList.length; ili++) {
    itemIdDictionary[imageryList[ili].itemId] = ili
  }
  return itemIdDictionary
}
const tileMapRequest = async ({ column, level, recentItemId, row, token, url }) => {
  let latestRelease = null
  let response = null
  let result = []
  try {
    response = await Axios({
      method: 'GET',
      params: {
        appid: token
      },
      signal: source.token,
      url: `${url}/${recentItemId}/${level}/${row}/${column}`
    })
  } catch (error) {
    console.error('Error tileMapRequest: ', error)
    return
  }

  if (response?.data) {
    const { data, select } = response.data
    if (select?.length > 0) {
      latestRelease = +select[0]
    } else {
      latestRelease = +recentItemId
    }
    if (data?.length > 0) {
      result = latestRelease
    }
  }

  return result
}
const getPreviousItemId = ({ imageryList, itemId, itemIdDictionary }) => {
  let previousItemId = null

  const itemIdIndex = itemIdDictionary[itemId]
  const imageryItem = imageryList[itemIdIndex + 1]

  if (imageryItem) {
    previousItemId = imageryList[itemIdIndex + 1].itemId
  } else {
    previousItemId = null
  }

  return previousItemId
}

const getItemIdsFromTileMap = async ({ column, imageryList, itemIdDictionary, level, row }) => {
  const [token, url] = getConfigs(['WAYBACK_REQUEST_TOKEN', 'WAYBACK_TILE_SERVICE_URL'])
  let recentItemId = imageryList[0].itemId
  let itemId = null
  let itemIds = []
  while (recentItemId) {
    try {
      itemId = await tileMapRequest({ column, imageryList, level, recentItemId, row, token, url })
    } catch (error) {
      console.error('Error getItemIdsFromTileMap.tileMapRequest: ', error)
    }
    if (itemId) {
      itemIds.push(itemId)
      recentItemId = getPreviousItemId({ imageryList, itemId, itemIdDictionary })
    } else {
      recentItemId = null
    }
  }
  return itemIds
}

/**** Step 4 ****/
const getSampledImagedDataUri = async ({ imageUrl, itemId }) => {
  let dataUri = ''
  let response = null
  const samplePoints = [512, 1000, 2500, 5000, 7500, 10000, 12500, 15000]

  try {
    response = await Axios({
      signal: source.token,
      method: 'GET',
      responseType: 'arraybuffer',
      url: imageUrl
    })
  } catch (error) {
    console.error('Error getSampledImagedDataUri: ', error)
    return
  }

  if (response?.data) {
    const uInt8Array = new Uint8Array(response.data)
    const binaryResults = []
    let ui = uInt8Array.length
    while (ui--) {
      binaryResults[ui] = String.fromCharCode(uInt8Array[ui])
    }

    const dataString = binaryResults.join('')
    const base64String = Buffer.from(dataString).toString('base64')

    for (const point of samplePoints) {
      dataUri += base64String.substring(point, 500)
    }
  }

  return {
    dataUri,
    itemId
  }
}
const removeDuplicates = async ({ candidates }) => {
  let uniqueResults = []
  let uri = []
  let uriRequests = []

  let ci = candidates.length - 1
  for (ci; ci >= 0; ci--) {
    uri = getSampledImagedDataUri({ imageUrl: candidates[ci].url, itemId: candidates[ci].itemId })
    uriRequests.push(uri)
  }

  let uriResults = []
  try {
    uriResults = await Promise.all(uriRequests)
  } catch (error) {
    console.error('Error removeDuplicates.getSampledImagedDataUri: ', error)
    return
  }

  let uriResult = null
  let uriPreviousRelease = null
  let ui = uriResults.length - 1
  for (ui; ui >= 0; ui--) {
    uriResult = uriResults[ui]
    if (uriResult.dataUri !== uriPreviousRelease) {
      uniqueResults.push(uriResult.itemId)
      uriPreviousRelease = uriResult.dataUri
    }
  }

  return uniqueResults
}
const setNewImageryList = ({ imageryList, uniqueCandidates }) => {
  const newUniqueCandidates = []
  let ili = 0
  let ill = imageryList.length
  for (ili; ili < ill; ili++) {
    if (uniqueCandidates.includes(imageryList[ili].itemId)) {
      newUniqueCandidates.push({
        ...imageryList[ili],
        index: ili
      })
    }
  }
  if (newUniqueCandidates.length > 0) {
    if (imageryList[0].itemId !== newUniqueCandidates[0].itemId) {
      newUniqueCandidates.unshift({
        ...imageryList[0],
        default: true
      })
    }
  }

  return newUniqueCandidates
}

/***********************************
 * getLayerMetadata Helper Functions
 ***********************************/
function convertToQueryString(obj, prefix = '') {
  const params = new URLSearchParams()

  for (const key in obj) {
    const value = obj[key]
    const paramKey = prefix ? `${prefix}[${key}]` : key

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      params.append(paramKey, JSON.stringify(value))
    } else if (Array.isArray(value)) {
      value.forEach(item => params.append(`${paramKey}[]`, item))
    } else {
      params.append(paramKey, value)
    }
  }

  return params.toString()
}
const setMetadata = features => {
  const { attributes } = features[0]
  return {
    accuracy: attributes.SRC_ACC,
    captureDate: attributes.SRC_DATE2,
    provider: attributes.NICE_DESC,
    resolution: attributes.SRC_RES,
    source: attributes.SRC_ACC
  }
}

/***********************************
 * Main functions
 ***********************************/
export const findChanges = async ({ imageryList, point }) => {
  let newImageryList = []
  let itemIds = []

  /**** Step 1 ****/
  const { column, level, row } = getTilesFromPointCoordinates({ point })

  /**** Step 2 ****/
  const itemIdDictionary = setItemIdDictionary({ imageryList })
  try {
    itemIds = await getItemIdsFromTileMap({ column, imageryList, itemIdDictionary, level, row })
  } catch (error) {
    console.error('Error findChanges.getItemIdsFromTileMap: ', error)
  }

  /**** Step 3 ****/
  if (itemIds?.length > 0) {
    if (!itemIds.includes(imageryList[0].itemId)) {
      itemIds.unshift(imageryList[0].itemId)
    }
    const candidates = []
    const replaceMap = { '{col}': column, '{level}': level, '{row}': row }
    const regexMatch = new RegExp(Object.keys(replaceMap).join('|'), 'gi')
    let itemIndex = null
    let urlTemplate = null
    let idi = 0
    let idl = itemIds.length
    for (idi; idi < idl; idi++) {
      itemIndex = itemIdDictionary[itemIds[idi]]
      if (itemIndex > -1) {
        urlTemplate = imageryList[itemIndex].itemURL.replace(regexMatch, matched => replaceMap[matched])
        candidates.push({
          itemId: itemIds[idi],
          url: urlTemplate
        })
      }
    }

    /**** Step 4 ****/
    if (candidates.length > 0) {
      let uniqueCandidates = []
      try {
        uniqueCandidates = await removeDuplicates({ candidates })
      } catch (error) {
        console.error('Error findChanges.removeDuplicates: ', error)
      }
      newImageryList = setNewImageryList({ imageryList, uniqueCandidates })
    }
  }

  return newImageryList
}

export const getLayerMetadata = async ({ geometry, item, zoom }) => {
  let metadata = null
  if (item) {
    const token = getConfig('WAYBACK_REQUEST_TOKEN')
    let request = null
    const zoomLevel = +zoom
    const zoomLevels = {
      min: 10,
      max: 23
    }
    const defaultZoomDiff = zoomLevels.max - zoomLevels.min
    let zoomDiff = zoomLevels.max - zoomLevel
    if (zoomDiff > defaultZoomDiff) {
      zoomDiff = defaultZoomDiff
    }
    const queryParams = {
      appid: token,
      f: 'json',
      geometry: geometry,
      geometryType: 'esriGeometryPoint',
      outFields: IMAGERY_METADATA_FIELDS.toString(),
      returnGeometry: false,
      spatialRel: 'esriSpatialRelIntersects',
      where: '1=1'
    }
    const queryString = convertToQueryString(queryParams)
    try {
      request = await Axios({
        method: 'GET',
        url: `${item.metadataURL}/${zoomDiff}/query/query?${queryString}`
      })
    } catch (error) {
      console.error('Error getLayerMetadata: ', error)
    }
    if (request.data?.features?.length > 0) {
      metadata = setMetadata(request.data.features)
    }
  }

  return metadata
}
